import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from "@angular/core";

import { MatrizEvidencia } from "../../shared/model/iso42001/matriz-riesgo.model";
import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { PantallaCompleta } from "../pantalla-completa";
import { Verificacion, iconoEvidencia, tamanoLegible, valorLegible } from "./expediente-formato";

/**
 * Sólo PDF, imágenes y video se abren en una pestaña, y con el tipo que manda
 * el servidor (sale de la extensión). El mime guardado no: en renglones viejos
 * lo declaraba el navegador al subir y podía ser text/html, que en un blob
 * corre con el origen de la app. Lo demás se baja.
 */
const SE_ABRE_EN_PESTANA = /^(application\/pdf|image\/(jpeg|png|gif|webp|bmp)|video\/mp4)$/;

/**
 * Lo que comparten el Expediente del activo y el Expediente del riesgo: la
 * ventana colgada de <body> (PantallaCompleta, porque la pestaña de Material
 * abre un contexto de apilamiento que la taparía), la exportación a PDF, las
 * evidencias adjuntas y la bitácora legible. Cada expediente pone sus datos,
 * su carga, su verificación de consistencia y su PDF.
 */
@Directive()
export abstract class ExpedienteBase implements OnInit, OnDestroy {
  @Output() cerrar = new EventEmitter<void>();

  exportando = false;

  protected readonly matrizServ = inject(MatrizRiesgoService);
  protected readonly cdr = inject(ChangeDetectorRef);
  private readonly fuera = new PantallaCompleta(inject<ElementRef<HTMLElement>>(ElementRef).nativeElement);

  /**
   * Las bitácoras guardan el nombre físico de la columna; sin su etiqueta el
   * usuario vería "fecha_alta". Cada expediente trae las de sus campos.
   */
  protected abstract readonly etiquetasCampo: Readonly<Record<string, string>>;

  ngOnInit(): void {
    this.fuera.sincronizar(true);
    this.cargar();
  }

  ngOnDestroy(): void {
    this.fuera.liberar();
  }

  /** Las consultas del expediente, cada sección por su lado. */
  protected abstract cargar(): void;

  /** Arma y descarga el PDF del expediente. */
  protected abstract generarPdf(): void;

  /** Los cruces que deben cuadrar, dichos uno por uno. */
  abstract verificaciones(): Verificacion[];

  get inconsistencias(): number {
    return this.verificaciones().filter((v) => !v.ok).length;
  }

  exportarPdf(): void {
    if (this.exportando) {
      return;
    }
    this.exportando = true;
    this.cdr.detectChanges();
    // En un setTimeout para que el "Generando…" alcance a pintarse: la
    // generación es síncrona y sin esto el botón nunca muestra el estado.
    setTimeout(() => {
      try {
        this.generarPdf();
      } finally {
        this.exportando = false;
        this.cdr.markForCheck();
      }
    });
  }

  /* ---------- Evidencias adjuntas ---------- */

  verEvidencia(evd: MatrizEvidencia): void {
    this.bajarEvidencia(evd, true);
  }

  guardarEvidencia(evd: MatrizEvidencia): void {
    this.bajarEvidencia(evd, false);
  }

  /**
   * Pide los bytes y los abre en una pestaña (si se pidió y el tipo lo permite) o los descarga. El tipo se lee
   * antes de crear la URL, en el mismo orden que tenían Ver y Descargar.
   */
  private bajarEvidencia(evd: MatrizEvidencia, abrir: boolean): void {
    if (!evd.idEvidencia) {
      return;
    }
    this.matrizServ.descargarEvidencia(evd.idEvidencia).subscribe((datos) => {
      const enPestana = abrir && SE_ABRE_EN_PESTANA.test((datos.type || "").toLowerCase());
      const url = URL.createObjectURL(datos);
      if (enPestana) {
        window.open(url, "_blank");
      } else {
        const liga = document.createElement("a");
        liga.href = url;
        liga.download = evd.nombreArchivo || "evidencia";
        liga.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    });
  }

  iconoEvidencia(evd: MatrizEvidencia): string {
    return iconoEvidencia(evd);
  }

  tamanoLegible(bytes?: number): string {
    return tamanoLegible(bytes);
  }

  /* ---------- Bitácora legible ---------- */

  /** Un campo no listado se muestra con espacios en lugar de guiones bajos, nunca crudo. */
  etiquetaCampo(campo?: string): string {
    if (!campo) {
      return "—";
    }
    return this.etiquetasCampo[campo] || campo.replaceAll("_", " ");
  }

  etiquetaAccionBit(accion?: string): string {
    switch (accion) {
      case "ALTA": return "Alta";
      case "MODIFICACION": return "Modificación";
      case "BAJA": return "Baja";
      case "REACTIVACION": return "Reactivación";
      default: return accion || "—";
    }
  }

  valorLegible(v?: string): string {
    return valorLegible(v);
  }
}
